import api from '@/services/api';
import { getApiErrorMessage } from '@/utils/apiError';
import { useSyncStore, getDefaultCatalogProgress, type CatalogSyncProgress } from '@/store/syncStore';
import {
  companyDb,
  ensureDeviceId,
  getCompanyCode,
  setMeta,
} from './companyContext';
import { stockKey, type CatalogCustomer, type CatalogItem, type CatalogSupplier, type LocalExpense, type LocalPurchase, type LocalSale, type PosSyncMode } from './db';
import { refreshQueueCounts } from './queue';

export const CATALOG_CHUNK_SIZE = 100;

export type { CatalogSyncProgress };
export type { CatalogSyncPhase } from '@/store/syncStore';

let catalogSyncInFlight = false;

function setCatalogProgress(patch: Partial<CatalogSyncProgress>) {
  const store = useSyncStore.getState();
  const current = store.catalogSyncProgress || getDefaultCatalogProgress();
  store.setCatalogSyncProgress({ ...current, ...patch, open: patch.open ?? current.open });
}

async function fetchMeta() {
  const res = await api.get('/method/saas_pos.api.offline_store.get_offline_catalog_meta');
  return res.data.message as {
    company: Record<string, unknown>;
    settings: Record<string, unknown> & { pos_sync_mode?: PosSyncMode };
    counts: {
      items: number;
      customers: number;
      suppliers: number;
      expenses?: number;
      sale_invoices?: number;
      purchase_invoices?: number;
    };
    categories: unknown[];
    uoms: unknown[];
    cash_pockets: unknown[];
    bank_accounts: unknown[];
    bootstrapped_at: string;
  };
}

type CatalogEntity =
  | 'items'
  | 'customers'
  | 'suppliers'
  | 'expenses'
  | 'sale_invoices'
  | 'purchase_invoices';

async function fetchChunk(entity: CatalogEntity, start: number, limit: number) {
  const res = await api.get('/method/saas_pos.api.offline_store.get_offline_catalog_chunk', {
    params: { entity, start, limit },
  });
  return res.data.message as {
    entity: string;
    rows: unknown[];
    start: number;
    limit: number;
    total: number;
    has_more: boolean;
  };
}

async function applyItemStock(items: CatalogItem[]) {
  const db = companyDb();
  for (const item of items) {
    if (item.item_type !== 'Product') continue;
    await db.local_stock.put({
      id: stockKey(item.name),
      item: item.name,
      qty: Number(item.stock_quantity) || 0,
    });
    const variants = (item.variants || []) as Array<{ name: string; stock_quantity?: number }>;
    for (const v of variants) {
      await db.local_stock.put({
        id: stockKey(item.name, v.name),
        item: item.name,
        variant: v.name,
        qty: Number(v.stock_quantity) || 0,
      });
    }
  }
}

/**
 * Download full server catalog into IndexedDB in chunks, with progress for the sync popup.
 */
export async function runChunkedCatalogSync(opts?: { force?: boolean }): Promise<{
  itemCount: number;
  customerCount: number;
  supplierCount: number;
  bootstrappedAt: string;
}> {
  if (catalogSyncInFlight) {
    throw new Error('Catalog sync already in progress');
  }
  catalogSyncInFlight = true;
  await ensureDeviceId();
  const db = companyDb();

  try {
    setCatalogProgress({
      open: true,
      phase: 'preparing',
      label: 'Preparing local catalog…',
      entityDone: 0,
      entityTotal: 0,
      overallDone: 0,
      overallTotal: 1,
      error: null,
    });

    setCatalogProgress({ phase: 'meta', label: 'Loading company settings…' });
    const meta = await fetchMeta();
    const counts = meta.counts || { items: 0, customers: 0, suppliers: 0 };
    const expenseTotal = Number(counts.expenses) || 0;
    const saleInvTotal = Number(counts.sale_invoices) || 0;
    const purchaseInvTotal = Number(counts.purchase_invoices) || 0;
    const overallTotal = Math.max(
      1,
      counts.items +
        counts.customers +
        counts.suppliers +
        expenseTotal +
        saleInvTotal +
        purchaseInvTotal,
    );
    let overallDone = 0;

    setCatalogProgress({
      phase: 'meta',
      label: 'Saving settings & categories…',
      overallTotal,
      overallDone: 0,
      entityDone: 0,
      entityTotal: 1,
    });

    // Clear catalog tables once before chunked insert
    await db.transaction('rw', [db.catalog_items, db.catalog_customers, db.catalog_suppliers, db.local_stock], async () => {
      await db.catalog_items.clear();
      await db.catalog_customers.clear();
      await db.catalog_suppliers.clear();
      await db.local_stock.clear();
    });

    await db.catalog_settings.put({ key: 'settings', value: meta.settings });
    await db.catalog_settings.put({ key: 'company', value: meta.company });
    await db.catalog_settings.put({ key: 'categories', value: meta.categories || [] });
    await db.catalog_settings.put({ key: 'uoms', value: meta.uoms || [] });
    await db.catalog_settings.put({ key: 'cash_pockets', value: meta.cash_pockets || [] });
    await db.catalog_settings.put({ key: 'bank_accounts', value: meta.bank_accounts || [] });
    await db.catalog_settings.put({ key: 'promotions', value: meta.promotions || [] });

    const mode = (meta.settings?.pos_sync_mode || 'Live Online') as PosSyncMode;
    useSyncStore.getState().setPosSyncMode(mode);

    // Items
    {
      const total = counts.items;
      let start = 0;
      setCatalogProgress({
        phase: 'items',
        label: `Downloading products… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('items', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as CatalogItem[];
        if (rows.length) {
          await db.catalog_items.bulkPut(rows);
          await applyItemStock(rows);
        }
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'items',
          label: `Downloading products… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    // Customers
    {
      const total = counts.customers;
      let start = 0;
      setCatalogProgress({
        phase: 'customers',
        label: `Downloading customers… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('customers', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as CatalogCustomer[];
        if (rows.length) await db.catalog_customers.bulkPut(rows);
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'customers',
          label: `Downloading customers… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    // Suppliers
    {
      const total = counts.suppliers;
      let start = 0;
      setCatalogProgress({
        phase: 'suppliers',
        label: `Downloading suppliers… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('suppliers', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as CatalogSupplier[];
        if (rows.length) await db.catalog_suppliers.bulkPut(rows);
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'suppliers',
          label: `Downloading suppliers… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    // Expenses (merge into local_expenses — keep unsynced local creates)
    {
      const total = expenseTotal;
      // Remove previously seeded server rows; keep local unsynced creates.
      const existing = await db.local_expenses.toArray();
      for (const row of existing) {
        if (row.seededFromServer || (row.serverName && row.clientOfflineId.startsWith('srv_exp_'))) {
          await db.local_expenses.delete(row.clientOfflineId);
        }
      }
      let start = 0;
      setCatalogProgress({
        phase: 'expenses',
        label: `Downloading expenses… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('expenses', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as Array<Record<string, unknown>>;
        if (rows.length) {
          const mapped: LocalExpense[] = rows.map((r) => {
            const serverName = String(r.name || '');
            const cid = String(r.client_offline_id || `srv_exp_${serverName}`);
            const posting = String(r.posting_date || r.creation || new Date().toISOString());
            return {
              clientOfflineId: cid,
              createdAt: posting.includes('T') ? posting : `${posting}T00:00:00.000Z`,
              amount: Number(r.amount) || 0,
              category: r.category ? String(r.category) : undefined,
              payment_method: r.payment_method ? String(r.payment_method) : undefined,
              cash_pocket: r.cash_pocket ? String(r.cash_pocket) : undefined,
              bank_account: r.bank_account ? String(r.bank_account) : undefined,
              remarks: r.remarks ? String(r.remarks) : undefined,
              serverName,
              recorded_by: r.recorded_by ? String(r.recorded_by) : undefined,
              recorded_by_name: r.recorded_by_name ? String(r.recorded_by_name) : undefined,
              status: String(r.status || 'Submitted'),
              seededFromServer: true,
            };
          });
          await db.local_expenses.bulkPut(mapped);
        }
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'expenses',
          label: `Downloading expenses… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    // Sale invoices → local_sales for offline ledgers (keep unsynced local sales)
    {
      const total = saleInvTotal;
      const existing = await db.local_sales.toArray();
      for (const row of existing) {
        if (row.serverInvoiceName && row.clientOfflineId.startsWith('srv_sale_')) {
          await db.local_sales.delete(row.clientOfflineId);
        }
      }
      let start = 0;
      setCatalogProgress({
        phase: 'sale_invoices',
        label: `Downloading sales… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('sale_invoices', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as Array<Record<string, unknown>>;
        if (rows.length) {
          const mapped: LocalSale[] = rows.map((r) => {
            const serverName = String(r.name || '');
            const cid = String(r.client_offline_id || `srv_sale_${serverName}`);
            const posting = String(r.posting_date || r.invoice_date || r.creation || '');
            const createdAt = posting
              ? posting.includes('T')
                ? posting
                : `${String(posting).slice(0, 10)}T00:00:00.000Z`
              : new Date().toISOString();
            return {
              clientOfflineId: cid,
              offlineInvoiceNo: String(r.offline_invoice_no || serverName),
              createdAt,
              customer: String(r.customer || ''),
              customerName: r.customer_name ? String(r.customer_name) : undefined,
              items: [],
              discount_amount: 0,
              tax_amount: 0,
              payments: [],
              net_total: Number(r.net_total) || 0,
              paid_amount: Number(r.paid_amount) || 0,
              status: String(r.status || 'Submitted'),
              serverInvoiceName: serverName,
            };
          });
          await db.local_sales.bulkPut(mapped);
        }
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'sale_invoices',
          label: `Downloading sales… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    // Purchase invoices → local_purchases for offline ledgers
    {
      const total = purchaseInvTotal;
      const existing = await db.local_purchases.toArray();
      for (const row of existing) {
        if (row.serverName && row.clientOfflineId.startsWith('srv_pur_')) {
          await db.local_purchases.delete(row.clientOfflineId);
        }
      }
      let start = 0;
      setCatalogProgress({
        phase: 'purchase_invoices',
        label: `Downloading purchases… 0 / ${total}`,
        entityDone: 0,
        entityTotal: total,
        overallDone,
        overallTotal,
      });
      while (start < total || (total === 0 && start === 0)) {
        if (total === 0) break;
        const chunk = await fetchChunk('purchase_invoices', start, CATALOG_CHUNK_SIZE);
        const rows = (chunk.rows || []) as Array<Record<string, unknown>>;
        if (rows.length) {
          const mapped: LocalPurchase[] = rows.map((r) => {
            const serverName = String(r.name || '');
            const cid = String(r.client_offline_id || `srv_pur_${serverName}`);
            const posting = String(r.posting_date || r.invoice_date || r.creation || '');
            const createdAt = posting
              ? posting.includes('T')
                ? posting
                : `${String(posting).slice(0, 10)}T00:00:00.000Z`
              : new Date().toISOString();
            const net = Number(r.net_total ?? r.grand_total) || 0;
            return {
              clientOfflineId: cid,
              createdAt,
              supplier: String(r.supplier || ''),
              supplierName: r.supplier_name ? String(r.supplier_name) : undefined,
              items: [],
              net_total: net,
              paid_amount: Number(r.paid_amount) || 0,
              serverName,
            };
          });
          await db.local_purchases.bulkPut(mapped);
        }
        start += rows.length || CATALOG_CHUNK_SIZE;
        overallDone = Math.min(overallTotal, overallDone + rows.length);
        const entityDone = Math.min(total, start);
        setCatalogProgress({
          phase: 'purchase_invoices',
          label: `Downloading purchases… ${entityDone} / ${total}`,
          entityDone,
          entityTotal: total,
          overallDone,
          overallTotal,
        });
        if (!chunk.has_more || rows.length === 0) break;
        await new Promise((r) => setTimeout(r, 50));
      }
    }

    setCatalogProgress({
      phase: 'finalizing',
      label: 'Finalizing local store…',
      entityDone: 1,
      entityTotal: 1,
      overallDone: overallTotal,
      overallTotal,
    });

    const syncedAt = meta.bootstrapped_at || new Date().toISOString();
    await setMeta('catalogSyncedAt', syncedAt);
    await setMeta('companyCode', getCompanyCode());
    useSyncStore.getState().setCatalogSyncedAt(syncedAt);
    await refreshQueueCounts();

    setCatalogProgress({
      phase: 'done',
      label: 'Local catalog ready',
      overallDone: overallTotal,
      overallTotal,
      entityDone: 1,
      entityTotal: 1,
    });

    // Keep success state visible briefly
    await new Promise((r) => setTimeout(r, 600));
    setCatalogProgress({ open: false, phase: 'done', label: '', error: null });

    void opts;
    return {
      itemCount: counts.items,
      customerCount: counts.customers,
      supplierCount: counts.suppliers,
      bootstrappedAt: syncedAt,
    };
  } catch (err) {
    const message = getApiErrorMessage(err, 'Catalog sync failed');
    setCatalogProgress({
      open: true,
      phase: 'error',
      label: message,
      error: message,
    });
    throw err;
  } finally {
    catalogSyncInFlight = false;
  }
}

/** Close the catalog sync popup (e.g. after error dismiss). */
export function closeCatalogSyncPopup() {
  setCatalogProgress({ open: false, phase: 'done', label: '', error: null });
}
